import {VideoStreamConfig} from './VideoStreamController';
import {Parameter} from '../../../types/model/framework/Parameter';
import {VideoMonitorNotification} from './VideoStreamMonitor';
import {VideoStreamSubject} from './VideoStreamSubject';
import {toast} from '../../../base-components/StudioToast';
import {IntlShape} from 'react-intl';

export class VideoServerClient {
  static HEARTBEAT_INTERVAL: number = 60 * 1000;
  static RETRY_PAUSE: number = 2000;
  static RETRY_ATTEMPTS: number = 10;

  socket: WebSocket | null = null;
  heartbeatInterval?: NodeJS.Timeout;
  streamSubject: VideoStreamSubject | null = null;
  intl: IntlShape;

  constructor(intl: IntlShape) {
    this.intl = intl;
  }

  async openConnection(url: string) {
    this.socket = await this._openConnection(url);
    console.log('Videoserver connection opened');
    this.heartbeatInterval = setInterval(() => {
      this.sendMessage({
        id: 'heartbeat',
      });
    }, VideoServerClient.HEARTBEAT_INTERVAL);
  }

  private _openConnection(url: string): Promise<WebSocket> {
    return new Promise<WebSocket>((resolve, reject) => {
      const ws = new WebSocket(url);
      ws.onopen = () => {
        resolve(ws);
      };
      ws.onerror = e => {
        reject(e);
      };
    });
  }

  closeConnection(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
    if (this.socket) {
      this.socket.close();
      this.socket = null;
      this.streamSubject = null;
      console.log('Videoserver connection closed');
    }
  }

  private sendMessage(msg: object, attempts = VideoServerClient.RETRY_ATTEMPTS): void {
    if (attempts === 0) {
      console.warn('Message missed: ' + JSON.stringify(msg));
    } else if (this.socket) {
      if (
        this.socket.readyState === WebSocket.CLOSING ||
        this.socket.readyState === WebSocket.CLOSED
      ) {
        this.streamSubject?.raiseConnectionTerminationEvent(
          'Error connecting to server.'
        );
        this.closeConnection();
      } else {
        this.socket.send(JSON.stringify(msg));
      }
    } else {
      setTimeout(
        () => this.sendMessage(msg, attempts - 1),
        VideoServerClient.RETRY_PAUSE
      );
    }
  }

  addHlsWebSocketListeners(subject: VideoStreamSubject, startCallback: () => void): void {
    if (this.socket) {
      this._addHlsWebSocketListeners(this.socket, subject, startCallback);
    }
  }

  private _addHlsWebSocketListeners(
    socket: WebSocket,
    subject: VideoStreamSubject,
    startCallback: () => void
  ): void {
    socket.onmessage = async message => {
      const event = JSON.parse(message.data);
      switch (event.id) {
        case 'data':
          subject.raiseDataEvent(event.data);
          break;
        case 'stopCommunication':
          console.debug('Communication ended by server');
          if (event.errorMessage) {
            subject.raiseConnectionTerminationEvent(event.errorMessage);
          } else if (event.expired) {
            toast.success({
              title: this.intl.formatMessage({id: 'stream.video.timeout.title'}),
              subtitle: this.intl.formatMessage(
                {id: 'stream.video.timeout.msg'},
                {duration: event.duration}
              ),
              position: 'top',
            });
            subject.raiseStreamFinishEvent();
          } else {
            subject.raiseStreamFinishEvent();
          }
          break;
        case 'startCommunication':
          if (startCallback) {
            startCallback();
          }
          break;
        case 'error':
          console.debug('Received error from server', event);
          subject.raiseConnectionTerminationEvent(event.message);
          break;
        default:
          break;
      }
    };
  }

  sendParameters(parameters: Array<Parameter>): void {
    this.sendMessage({
      id: 'parameters',
      parameters,
    });
  }

  sendStatistics(notification: VideoMonitorNotification): void {
    this.sendMessage({
      id: 'statistics',
      projectId: notification.projectId,
      deploymentId: notification.deploymentId,
      kbps: notification.kbps,
      fps: notification.fps,
    });
  }

  registerHlsConnection(config: VideoStreamConfig): void {
    const maxDuration =
      config.limitedDuration && config.serverConfig.spaVideoSessionDuration
        ? config.serverConfig.spaVideoSessionDuration
        : undefined;
    this.streamSubject = config.streamSubject;

    this.sendMessage({
      id: 'connect',
      sessionType: 'HLS_MEDIA',
      streamModel: config.streamModel,
      source: config.source,
      videoUrl: config.url,
      sourceUrl: config.sourceUrl,
      targetSession: config.targetSession,
      projectId: config.projectId,
      deploymentId: config.monitorConfig.deploymentId,
      internalSource: config.internalSource,
      internalNetwork: config.internalNetwork,
      duration: maxDuration,
    });
  }
}
